import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  User, Lock, Bell, TrendingUp, ExternalLink,
  CheckCircle, AlertCircle, RefreshCw, Users, Trash2, Mail,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Layout from '../components/Layout';

const P  = '#C29500';
const P2 = '#E8C040';
const G  = '#E8C040';
const R  = '#EF4444';
const C  = '#06B6D4';

const CARD = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
};

const INNER = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 10,
};

const inputStyle = {
  width: '100%', background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10, padding: '11px 14px', color: '#fff', fontSize: 14,
  outline: 'none', boxSizing: 'border-box', transition: 'border-color .2s',
};

function Field({ label, hint, children }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
        {hint && <span style={{ fontSize: 11, color: '#334155' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

const EXNESS_LINK = 'https://one.exnessonelink.com/a/g6ljezxa3c';
const ALL_TABS = [
  { id: 'profile',       label: 'Profile',   icon: <User size={14}/> },
  { id: 'team',          label: 'Team',      icon: <Users size={14}/>, ownerOnly: true },
  { id: 'trading',       label: 'Trading',   icon: <TrendingUp size={14}/> },
  { id: 'security',      label: 'Security',  icon: <Lock size={14}/> },
  { id: 'notifications', label: 'Alerts',    icon: <Bell size={14}/> },
];

export default function Settings() {
  const [profile,    setProfile]    = useState({ full_name: '', phone: '', country: '', tg_chat_id: '' });
  const [passwords,  setPasswords]  = useState({ old_password: '', new_password: '' });
  const [exness,     setExness]     = useState({ login: '', password: '', server: '' });
  const [trading,    setTrading]    = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [tab,        setTab]        = useState('profile');
  const [twofa,      setTwofa]      = useState(false);
  const [twofaSaving,setTwofaSaving]= useState(false);
  const [role,       setRole]       = useState('owner');
  const [team,       setTeam]       = useState({ members: [], pending: [] });
  const [invite,     setInvite]     = useState({ email: '', full_name: '', role: 'viewer' });
  const [inviting,   setInviting]   = useState(false);

  const TABS = ALL_TABS.filter(t => !t.ownerOnly || role === 'owner');
  const ROLE_LABELS = { owner: 'Owner', finance_manager: 'Finance Manager', viewer: 'Viewer', developer: 'Developer' };

  useEffect(() => { loadData(); }, []);

  const loadTeam = async () => {
    try { const r = await api.get('/team/members'); setTeam(r.data); }
    catch { /* not an owner / no team */ }
  };

  const sendInvite = async () => {
    if (!invite.email.trim()) { toast.error('Enter an email'); return; }
    setInviting(true);
    try {
      await api.post('/team/invite', invite);
      toast.success('Invite sent!');
      setInvite({ email: '', full_name: '', role: 'viewer' });
      loadTeam();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to invite'); }
    finally { setInviting(false); }
  };

  const changeRole = async (userId, newRole) => {
    try { await api.put(`/team/member/${userId}/role`, { role: newRole }); toast.success('Role updated'); loadTeam(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Remove this team member? They will lose access.')) return;
    try { await api.delete(`/team/member/${userId}`); toast.success('Member removed'); loadTeam(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const revokeInvite = async (inviteId) => {
    try { await api.delete(`/team/invite/${inviteId}`); toast.success('Invite revoked'); loadTeam(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggle2FA = async () => {
    const next = !twofa;
    setTwofaSaving(true);
    try {
      await api.put('/user/2fa', { enabled: next });
      setTwofa(next);
      toast.success(`Two-factor authentication ${next ? 'enabled' : 'disabled'}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update 2FA');
    } finally { setTwofaSaving(false); }
  };

  const loadData = async () => {
    try {
      const [profileRes, tradingRes] = await Promise.all([
        api.get('/user/profile'),
        api.get('/trading/account-info').catch(() => null),
      ]);
      setProfile({
        full_name  : profileRes.data.full_name  || '',
        phone      : profileRes.data.phone      || '',
        country    : profileRes.data.country    || '',
        tg_chat_id : profileRes.data.tg_chat_id || '',
      });
      setTwofa(!!profileRes.data.twofa_enabled);
      const r = profileRes.data.role || 'owner';
      setRole(r);
      if (r === 'owner') loadTeam();
      if (tradingRes) setTrading(tradingRes.data);
    } catch {
      toast.error('Failed to load settings');
    }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      await api.put('/user/profile', profile);
      toast.success('Profile updated!');
    } catch {
      toast.error('Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async () => {
    if (!passwords.old_password || !passwords.new_password) { toast.error('Fill all fields'); return; }
    setLoading(true);
    try {
      await api.put('/user/change-password', passwords);
      toast.success('Password changed!');
      setPasswords({ old_password: '', new_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const connectExness = async () => {
    if (!exness.login || !exness.password || !exness.server) { toast.error('Fill all details'); return; }
    setConnecting(true);
    try {
      const res = await api.post('/trading/connect-exness', exness);
      toast.success('Exness account connected!');
      setTrading(res.data);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const disconnectExness = async () => {
    try {
      await api.delete('/trading/disconnect');
      toast.success('Account disconnected');
      setTrading(null);
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const refreshTrading = async () => {
    try {
      const res = await api.get('/trading/account-info');
      setTrading(res.data);
      toast.success('Refreshed!');
    } catch {
      toast.error('Failed to refresh');
    }
  };

  const Input = ({ type = 'text', value, onChange, placeholder }) => (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={inputStyle}
      onFocus={e => e.target.style.borderColor = `${P}50`}
      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
    />
  );

  const SaveBtn = ({ onClick, label }) => (
    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
      onClick={onClick} disabled={loading}
      style={{
        width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
        background: `linear-gradient(135deg,${P},${P2})`,
        color: '#000', fontSize: 14, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      }}>
      {loading
        ? <div style={{ width: 18, height: 18, border: '2px solid #000', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
        : label}
    </motion.button>
  );

  return (
    <Layout>
      <div style={{ padding: 28, maxWidth: 760, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: -0.5 }}>Settings</h1>
          <p style={{ fontSize: 13, color: '#64748B', margin: '6px 0 0' }}>Manage your account</p>
        </motion.div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '9px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: tab === t.id ? `linear-gradient(135deg,${P},${P2})` : 'rgba(255,255,255,0.04)',
                border: tab === t.id ? 'none' : '1px solid rgba(255,255,255,0.08)',
                color: tab === t.id ? '#000' : '#64748B',
              }}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {tab === 'profile' && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            style={{ ...CARD, padding: 28 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <User size={15} style={{ color: P2 }}/> Profile Settings
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Field label="Business Name">
                <Input value={profile.full_name} onChange={e => setProfile({ ...profile, full_name: e.target.value })} placeholder="Your business name"/>
              </Field>
              <Field label="Phone Number">
                <Input type="tel" value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} placeholder="+234 XXX XXX XXXX"/>
              </Field>
              <Field label="Country">
                <Input value={profile.country} onChange={e => setProfile({ ...profile, country: e.target.value })} placeholder="Nigeria"/>
              </Field>
              <Field label="Telegram Chat ID" hint="Get from @userinfobot on Telegram">
                <Input value={profile.tg_chat_id} onChange={e => setProfile({ ...profile, tg_chat_id: e.target.value })} placeholder="Your Telegram Chat ID"/>
              </Field>
              <SaveBtn onClick={saveProfile} label="Save Profile"/>
            </div>
          </motion.div>
        )}

        {/* Trading Tab */}
        {tab === 'trading' && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {trading?.is_connected ? (
              <div style={{ ...CARD, padding: 28, border: `1px solid ${G}30` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircle size={15} style={{ color: G }}/> Exness Account Connected
                  </p>
                  <motion.button whileHover={{ rotate: 180 }} transition={{ duration: 0.3 }}
                    onClick={refreshTrading}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', display: 'flex' }}>
                    <RefreshCw size={16}/>
                  </motion.button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
                  {[
                    { label: 'Login',    value: trading.login },
                    { label: 'Server',   value: trading.server },
                    { label: 'Balance',  value: `$${trading.balance?.toFixed(2) || '0.00'}` },
                    { label: 'Equity',   value: `$${trading.equity?.toFixed(2) || '0.00'}` },
                    { label: 'Margin',   value: `$${trading.margin?.toFixed(2) || '0.00'}` },
                    { label: 'Currency', value: trading.currency || 'USD' },
                  ].map((item, i) => (
                    <div key={i} style={{ ...INNER, padding: '12px 14px' }}>
                      <p style={{ fontSize: 10, color: '#475569', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</p>
                      <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0 }}>{item.value}</p>
                    </div>
                  ))}
                </div>

                <motion.button whileHover={{ scale: 1.02 }} onClick={disconnectExness}
                  style={{
                    width: '100%', padding: '12px 0', borderRadius: 10, cursor: 'pointer',
                    background: `${R}10`, border: `1px solid ${R}30`,
                    color: R, fontSize: 14, fontWeight: 600,
                  }}>
                  Disconnect Account
                </motion.button>
              </div>
            ) : (
              <>
                {/* Step 1 */}
                <div style={{ ...CARD, padding: 28 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      width: 26, height: 26, borderRadius: '50%', background: `linear-gradient(135deg,${P},${P2})`,
                      color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0,
                    }}>1</span>
                    Create Exness Account
                  </h3>
                  <p style={{ fontSize: 13, color: '#64748B', margin: '0 0 20px', lineHeight: 1.6 }}>
                    Create your free Exness trading account. Takes less than 2 minutes!
                  </p>

                  <div style={{ ...INNER, padding: 16, marginBottom: 20 }}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {['Free to open account','Instant account activation','Minimum deposit $10','MT5 platform included','Leverage up to 1:2000'].map((item, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#CBD5E1' }}>
                          <CheckCircle size={13} style={{ color: G, flexShrink: 0 }}/>{item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <motion.a href={EXNESS_LINK} target="_blank" rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '13px 0', borderRadius: 10, textDecoration: 'none',
                      background: `linear-gradient(135deg,${P},${P2})`,
                      color: '#000', fontSize: 14, fontWeight: 700,
                    }}>
                    <ExternalLink size={16}/> Open Exness Account Free
                  </motion.a>
                  <p style={{ fontSize: 11, color: '#334155', textAlign: 'center', marginTop: 8 }}>Opens in new tab · Takes 2 minutes</p>
                </div>

                {/* Step 2 */}
                <div style={{ ...CARD, padding: 28 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      width: 26, height: 26, borderRadius: '50%', background: `linear-gradient(135deg,${C},${C}90)`,
                      color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0,
                    }}>2</span>
                    Connect Your Account
                  </h3>
                  <p style={{ fontSize: 13, color: '#64748B', margin: '0 0 20px' }}>Enter your MT5 login details below.</p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Field label="MT5 Login Number">
                      <Input value={exness.login} onChange={e => setExness({ ...exness, login: e.target.value })} placeholder="e.g. 12345678"/>
                    </Field>
                    <Field label="MT5 Password">
                      <Input type="password" value={exness.password} onChange={e => setExness({ ...exness, password: e.target.value })} placeholder="Your MT5 password"/>
                    </Field>
                    <Field label="Server Name">
                      <Input value={exness.server} onChange={e => setExness({ ...exness, server: e.target.value })} placeholder="e.g. Exness-MT5Real8"/>
                      <p style={{ fontSize: 11, color: '#334155', marginTop: 4 }}>Find in MT5 app → File → Login → Server dropdown</p>
                    </Field>

                    <div style={{ ...INNER, padding: 14, background: '#F59E0B08', border: '1px solid #F59E0B25' }}>
                      <p style={{ fontSize: 12, fontWeight: 700, color: '#F59E0B', margin: '0 0 4px' }}>⚠️ Use Investor Password</p>
                      <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>
                        For security, use your MT5 Investor (read-only) password. This allows us to read data without making manual trades.
                      </p>
                    </div>

                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      onClick={connectExness} disabled={connecting}
                      style={{
                        width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                        background: `linear-gradient(135deg,${P},${P2})`,
                        color: '#000', fontSize: 14, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      }}>
                      {connecting
                        ? <><div style={{ width: 18, height: 18, border: '2px solid #000', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/> Connecting…</>
                        : <><TrendingUp size={16}/> Connect Exness Account</>
                      }
                    </motion.button>
                  </div>
                </div>

                {/* Help */}
                <div style={{ ...CARD, padding: 24 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertCircle size={14} style={{ color: C }}/> Where to find your details?
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      'Login to my.exness.com',
                      'Go to My Accounts section',
                      'Click on your MT5 account',
                      'Find Login number and Server name',
                      'Use your MT5 trading password',
                    ].map((text, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#64748B' }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%',
                          background: `${P}14`, color: P2,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 800, flexShrink: 0,
                        }}>{i + 1}</div>
                        {text}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* Security Tab */}
        {tab === 'security' && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            style={{ ...CARD, padding: 28 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Lock size={15} style={{ color: P2 }}/> Change Password
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Field label="Current Password">
                <Input type="password" value={passwords.old_password} onChange={e => setPasswords({ ...passwords, old_password: e.target.value })} placeholder="Current password"/>
              </Field>
              <Field label="New Password">
                <Input type="password" value={passwords.new_password} onChange={e => setPasswords({ ...passwords, new_password: e.target.value })} placeholder="New password (min 6 chars)"/>
              </Field>
              <SaveBtn onClick={changePassword} label="Change Password"/>
            </div>

            {/* Two-Factor Authentication */}
            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '24px 0' }}/>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Lock size={15} style={{ color: P2 }}/> Two-Factor Authentication
            </p>
            <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 16px' }}>
              When enabled, we'll email you a 6-digit code to confirm each login.
            </p>
            <div style={{ ...INNER, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: '0 0 2px' }}>Email login code</p>
                <p style={{ fontSize: 12, color: twofa ? '#22C55E' : '#64748B', margin: 0 }}>
                  {twofa ? '● Enabled' : '○ Disabled'}
                </p>
              </div>
              <div onClick={twofaSaving ? undefined : toggle2FA}
                style={{
                  width: 44, height: 24, borderRadius: 12, position: 'relative',
                  cursor: twofaSaving ? 'default' : 'pointer', opacity: twofaSaving ? 0.6 : 1,
                  background: twofa ? `linear-gradient(135deg,${P},${P2})` : 'rgba(255,255,255,0.1)',
                  transition: 'background .2s',
                }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute',
                  top: 3, left: twofa ? 23 : 3, transition: 'left .2s',
                }}/>
              </div>
            </div>
          </motion.div>
        )}

        {/* Team Tab */}
        {tab === 'team' && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Invite */}
            <div style={{ ...CARD, padding: 24 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Mail size={15} style={{ color: P2 }}/> Invite a team member
              </p>
              <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 18px' }}>
                They'll get an email link to set a password and join your business account.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <Field label="Full Name"><Input value={invite.full_name} onChange={e => setInvite({ ...invite, full_name: e.target.value })} placeholder="Jane Doe"/></Field>
                <Field label="Email"><Input type="email" value={invite.email} onChange={e => setInvite({ ...invite, email: e.target.value })} placeholder="jane@company.com"/></Field>
              </div>
              <Field label="Role">
                <select value={invite.role} onChange={e => setInvite({ ...invite, role: e.target.value })}
                  style={{ width: '100%', padding: '11px 12px', borderRadius: 10, background: '#0D0D12', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', fontSize: 14 }}>
                  <option value="finance_manager">Finance Manager — view + initiate payouts</option>
                  <option value="viewer">Viewer — read-only reports</option>
                  <option value="developer">Developer — API keys only</option>
                  <option value="owner">Owner — full control</option>
                </select>
              </Field>
              <div style={{ marginTop: 16 }}>
                <SaveBtn onClick={sendInvite} label={inviting ? 'Sending…' : 'Send Invite'}/>
              </div>
            </div>

            {/* Members */}
            <div style={{ ...CARD, padding: 24 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Users size={15} style={{ color: P2 }}/> Team members
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {team.owner && (
                  <div style={{ ...INNER, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div><p style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: '0 0 2px' }}>{team.owner.full_name} <span style={{ color: P2, fontSize: 11 }}>(you)</span></p>
                      <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>{team.owner.email}</p></div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: P2, padding: '4px 10px', borderRadius: 20, background: `${P2}14` }}>Owner</span>
                  </div>
                )}
                {(team.members || []).map(m => (
                  <div key={m.id} style={{ ...INNER, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 140 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: '0 0 2px' }}>{m.full_name}</p>
                      <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>{m.email}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <select value={m.role} onChange={e => changeRole(m.id, e.target.value)}
                        style={{ padding: '6px 10px', borderRadius: 8, background: '#0D0D12', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }}>
                        {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      <button onClick={() => removeMember(m.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', display: 'flex' }}><Trash2 size={15}/></button>
                    </div>
                  </div>
                ))}
                {(team.pending || []).map(p => (
                  <div key={`inv-${p.id}`} style={{ ...INNER, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, opacity: 0.85 }}>
                    <div><p style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: '0 0 2px' }}>{p.full_name || p.email}</p>
                      <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>{p.email}</p></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#F59E0B', padding: '4px 10px', borderRadius: 20, background: '#F59E0B14' }}>Pending · {p.role_label}</span>
                      <button onClick={() => revokeInvite(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', display: 'flex' }}><Trash2 size={15}/></button>
                    </div>
                  </div>
                ))}
                {!team.members?.length && !team.pending?.length && (
                  <p style={{ fontSize: 13, color: '#475569', textAlign: 'center', padding: '20px 0' }}>No team members yet. Invite someone above.</p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Notifications Tab */}
        {tab === 'notifications' && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            style={{ ...CARD, padding: 28 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Bell size={15} style={{ color: P2 }}/> Notification Settings
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Telegram Alerts', desc: 'Trade opens/closes, deposits, withdrawals' },
                { label: 'Email Notifications', desc: 'Account updates and monthly reports' },
              ].map((item, i) => (
                <div key={i} style={{ ...INNER, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: '0 0 2px' }}>{item.label}</p>
                    <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>{item.desc}</p>
                  </div>
                  <div style={{
                    width: 44, height: 24, background: `linear-gradient(135deg,${P},${P2})`,
                    borderRadius: 12, position: 'relative', cursor: 'pointer',
                  }}>
                    <div style={{ width: 20, height: 20, background: '#fff', borderRadius: '50%', position: 'absolute', right: 2, top: 2 }}/>
                  </div>
                </div>
              ))}

              <div style={{ ...INNER, padding: '16px' }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: '0 0 10px' }}>Your Telegram Chat ID</p>
                <Input value={profile.tg_chat_id} onChange={e => setProfile({ ...profile, tg_chat_id: e.target.value })} placeholder="e.g. 123456789"/>
                <p style={{ fontSize: 11, color: '#334155', margin: '8px 0 14px' }}>
                  Get your ID from <span style={{ color: P2 }}>@userinfobot</span> on Telegram
                </p>
                <SaveBtn onClick={saveProfile} label="Save Telegram ID"/>
              </div>
            </div>
          </motion.div>
        )}

      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #334155; }
      `}</style>
    </Layout>
  );
}
